import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import FocusTrap from 'focus-trap-react';

/**
 * Accessible modal dialog component with focus trapping and keyboard navigation.
 *
 * Accessibility features:
 * - Focus is trapped inside the modal while open (via focus-trap-react)
 * - Escape key closes the modal (defense-in-depth: both focus-trap and keydown listener)
 * - Clicking the overlay background closes the modal (configurable via closeOnOverlay)
 * - ARIA role="dialog" and aria-modal="true" announce the modal to screen readers
 * - aria-labelledby links the dialog to its title for screen reader context
 * - Close button has aria-label for screen readers
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {function} props.onClose - Callback to close the modal
 * @param {string} props.title - Title displayed in the modal header
 * @param {React.ReactNode} props.children - Modal body content
 * @param {string} [props.subtitle] - Secondary text rendered below the title
 * @param {React.ReactNode} [props.headerActions] - Content rendered in the header right side (e.g., PDF buttons)
 * @param {React.ReactNode} [props.footer] - Content rendered in a footer div below the body
 * @param {'small'|'medium'|'large'|'full'} [props.size='medium'] - Controls the max-width of the modal
 * @param {boolean} [props.closeOnOverlay=true] - Whether clicking the overlay closes the modal
 * @param {function} [props.onKeyDown] - Additional keyboard handler called after Escape check
 * @param {string} [props.className] - Additional CSS class appended to modal-content div
 * @returns {JSX.Element|null} The modal element, or null when closed
 */
function Modal({
  isOpen,
  onClose,
  title,
  children,
  subtitle,
  headerActions,
  footer,
  size = 'medium',
  closeOnOverlay = true,
  onKeyDown,
  className,
}) {
  /**
   * Handle keyboard events: Escape closes, then delegate to onKeyDown.
   *
   * @param {KeyboardEvent} e - The keyboard event
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  }, [onClose, onKeyDown]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  // Render via portal to document.body so the modal is NOT nested inside
  // any ancestor <form>. Mark UAT 2026-05-27 (GH #148): when QuickAddCategoryModal
  // was rendered inline inside InvoiceEntry's <form>, clicking the modal's
  // "Add Category" submit button silently submitted the OUTER form (HTML
  // forbids nested forms — the inner <form> is unwrapped by browsers). With
  // editingInvoiceId set, the outer submit fired navigate('../invoices'),
  // wiping the user's in-progress edit. Portaling breaks the DOM nesting.
  return createPortal((
    <div
      className="modal-overlay"
      onClick={closeOnOverlay ? onClose : undefined}
      role="presentation"
    >
      <FocusTrap focusTrapOptions={{
        clickOutsideDeactivates: true,
        escapeDeactivates: true,
        fallbackFocus: '[role="dialog"]'
      }}>
        <div
          className={`modal-content modal-content--${size}${className ? ` ${className}` : ''}`}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          tabIndex="-1"
        >
          <div className="modal-header">
            <div className="modal-header__left">
              <h2 id="modal-title" className="modal-title">{title}</h2>
              {subtitle && <p className="modal-subtitle">{subtitle}</p>}
            </div>
            <div className="modal-header__right">
              {headerActions}
              <button
                className="modal-close"
                onClick={onClose}
                aria-label="Close dialog"
                type="button"
              >
                <svg
                  className="modal-close__icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
          <div className="modal-body">
            {children}
          </div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </FocusTrap>
    </div>
  ), document.body);
}

Modal.propTypes = {
  /** Whether the modal is visible */
  isOpen: PropTypes.bool.isRequired,
  /** Callback to close the modal */
  onClose: PropTypes.func.isRequired,
  /** Title displayed in the modal header */
  title: PropTypes.string.isRequired,
  /** Modal body content */
  children: PropTypes.node,
  /** Secondary text rendered below the title */
  subtitle: PropTypes.string,
  /** Content rendered in the header right side */
  headerActions: PropTypes.node,
  /** Content rendered in a footer div below the body */
  footer: PropTypes.node,
  /** Controls the max-width of the modal */
  size: PropTypes.oneOf(['small', 'medium', 'large', 'full']),
  /** Whether clicking the overlay closes the modal */
  closeOnOverlay: PropTypes.bool,
  /** Additional keyboard handler called after Escape check */
  onKeyDown: PropTypes.func,
  /** Additional CSS class appended to modal-content div */
  className: PropTypes.string,
};

export default Modal;
